import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Flex, Heading, Text, Switch, Input, InputGroup, InputLeftElement,
    Button, useToast, VStack, HStack, Table, Thead, Tbody, Tr, Th, Td,
    Badge, IconButton, Divider, Tooltip, TableContainer, RadioGroup, Radio,
    Stack, Alert, AlertIcon, Spinner, Textarea,
    Tabs, TabList, Tab, TabPanels, TabPanel,
} from '@chakra-ui/react';
import { FaTrash, FaPlus, FaSearch, FaRobot, FaSyncAlt, FaClipboardCheck, FaWhatsapp, FaPhoneAlt, FaQuestionCircle, FaHeart, FaClock, FaUserClock } from 'react-icons/fa';
import {
    getBotAtendimento, updateBotAtendimentoConfig, buscarContatosBot,
    adicionarContatoBot, atualizarContatoBot, removerContatoBot,
    verificarConversasBot, responderVerificacaoBot, responderTodasVerificacaoBot,
    getDesempenhoDepartamentosConfigs, updateDesempenhoDepartamento,
} from '../services/api';
import Preloader from '../components/Preloader';
import BotLigacaoTab from './BotLigacaoTab';
import BotPerguntasMlTab from './BotPerguntasMlTab';
import PosVendaTab from './PosVendaTab';
import RetiraAtrasoTab from './RetiraAtrasoTab';
import BotLimiteCotacaoTab from './BotLimiteCotacaoTab';

const ACCENT = '#F68633';

// O `slug` é o deep-link (?aba=...): é por ele que o radar do dashboard abre
// a tela já na aba certa, em vez de cair sempre no WhatsApp.
const ABAS = [
    { slug: 'whatsapp', icone: FaWhatsapp, curto: 'WhatsApp', longo: 'WhatsApp (Poli)' },
    { slug: 'ligacoes', icone: FaPhoneAlt, curto: 'Ligações', longo: 'Ligações (Voz)' },
    { slug: 'perguntas', icone: FaQuestionCircle, curto: 'Perguntas', longo: 'Perguntas ML' },
    { slug: 'posvenda', icone: FaHeart, curto: 'Pós-venda', longo: 'Pós-venda' },
    { slug: 'atrasos', icone: FaClock, curto: 'Atrasos', longo: 'Retirar Atrasos' },
    { slug: 'cotacoes', icone: FaUserClock, curto: 'Cotações', longo: 'Limite de cotações' },
];

const DIAS_SEMANA = [
    { v: 1, label: 'Seg' }, { v: 2, label: 'Ter' }, { v: 3, label: 'Qua' },
    { v: 4, label: 'Qui' }, { v: 5, label: 'Sex' }, { v: 6, label: 'Sáb' },
    { v: 0, label: 'Dom' },
];

const gradeDefault = () =>
    [0, 1, 2, 3, 4, 5, 6].map(() => ({ ativo: true, inicio: '08:00', fim: '18:00' }));

const normalizarGrade = (g) => {
    const base = gradeDefault();
    if (Array.isArray(g)) {
        g.forEach((d, i) => {
            if (i < 7 && d && typeof d === 'object') {
                base[i] = {
                    ativo: !!d.ativo,
                    inicio: d.inicio || '08:00',
                    fim: d.fim || '18:00',
                };
            }
        });
    }
    return base;
};

const BotAtendimento = () => {
    const toast = useToast();
    // Aba inicial pelo ?aba= (deep-link do radar). Slug desconhecido ou ausente
    // cai no WhatsApp, que é a aba padrão da tela.
    const [searchParams] = useSearchParams();
    const abaInicial = Math.max(0, ABAS.findIndex((a) => a.slug === searchParams.get('aba')));
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState({
        ativo: false, modo: 'selecionados', modo_todos_desde: null,
        horario_restrito: false, horario_dias: gradeDefault(), system_prompt: '',
    });
    const [systemPromptPadrao, setSystemPromptPadrao] = useState('');
    const [contatos, setContatos] = useState([]);
    const [logs, setLogs] = useState([]);
    const [verificacao, setVerificacao] = useState({ status: null, itens: [] });
    const [savingConfig, setSavingConfig] = useState(false);
    const [salvandoPrompt, setSalvandoPrompt] = useState(false);
    const [verificando, setVerificando] = useState(false);

    // Busca de contatos na Poli
    const [busca, setBusca] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [resultados, setResultados] = useState([]);

    // Números de notificação de transferência por departamento
    const [departamentos, setDepartamentos] = useState([]);
    const [salvandoDepto, setSalvandoDepto] = useState(null);

    const carregar = useCallback(async (comLoading = false, incluirConfig = false) => {
        if (comLoading) setLoading(true);
        try {
            const { data } = await getBotAtendimento();
            // Config só é recarregada no load inicial — evita sobrescrever edições em andamento
            if (incluirConfig) {
                const cfg = data.config || {
                    ativo: false, modo: 'selecionados', modo_todos_desde: null,
                    horario_restrito: false,
                };
                setConfig({
                    ...cfg,
                    horario_dias: normalizarGrade(cfg.horario_dias),
                    system_prompt: cfg.system_prompt || '',
                });
                setSystemPromptPadrao(data.system_prompt_padrao || '');
            }
            setContatos(data.contatos || []);
            setLogs(data.logs || []);
            setVerificacao(data.verificacao || { status: null, itens: [] });
        } catch (e) {
            toast({ title: 'Erro ao carregar os dados do bot.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            if (comLoading) setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        carregar(true, true);
        // Atualiza a tela periodicamente para acompanhar a atividade do bot
        const t = setInterval(() => carregar(false), 20000);
        return () => clearInterval(t);
    }, [carregar]);

    // Carrega os departamentos (para os números de notificação de transferência)
    useEffect(() => {
        let ativo = true;
        getDesempenhoDepartamentosConfigs()
            .then((resp) => {
                if (!ativo) return;
                const lista = Array.isArray(resp?.data) ? resp.data : [];
                // Por ora só o Suporte (158625) recebe transferências do bot.
                setDepartamentos(lista.filter((d) => Number(d.departamento_id_poli) === 158625));
            })
            .catch(() => {});
        return () => { ativo = false; };
    }, []);

    const salvarNumeroDepto = async (dep) => {
        setSalvandoDepto(dep.id);
        try {
            const res = await updateDesempenhoDepartamento(dep.id, { whatsapp_notificacao: dep.whatsapp_notificacao || '' });
            if (res && res.data && res.data.id) {
                toast({ title: `Número do setor ${dep.nome} salvo.`, status: 'success', duration: 3000, isClosable: true });
            } else {
                toast({ title: 'Erro ao salvar o número do setor.', status: 'error', duration: 4000, isClosable: true });
            }
        } catch (e) {
            toast({ title: 'Erro ao salvar o número do setor.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setSalvandoDepto(null);
        }
    };

    const salvarConfig = async (overrides, mensagem) => {
        setSavingConfig(true);
        try {
            const payload = {
                ativo: config.ativo,
                modo: config.modo,
                horario_restrito: config.horario_restrito,
                horario_dias: config.horario_dias,
                ...overrides,
            };
            const { data } = await updateBotAtendimentoConfig(payload);
            setConfig({
                ...data,
                horario_dias: normalizarGrade(data?.horario_dias),
                system_prompt: data?.system_prompt || '',
            });
            if (mensagem) {
                toast({ title: mensagem, status: 'success', duration: 3000, isClosable: true });
            }
        } catch (e) {
            toast({ title: 'Erro ao salvar a configuração do bot.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setSavingConfig(false);
        }
    };

    const salvarPrompt = async () => {
        setSalvandoPrompt(true);
        try {
            await salvarConfig(
                { system_prompt: config.system_prompt || null },
                config.system_prompt
                    ? 'Prompt salvo.'
                    : 'Prompt limpo — o bot voltou a usar o padrão.'
            );
        } finally {
            setSalvandoPrompt(false);
        }
    };

    const carregarPadraoNoCampo = () => {
        setConfig((c) => ({ ...c, system_prompt: systemPromptPadrao }));
    };

    const toggleBot = (ativo) => {
        salvarConfig({ ativo }, ativo ? 'Bot ativado.' : 'Bot desativado.');
    };

    const handleModo = (modo) => {
        if (modo === config.modo) return;
        if (modo === 'todos') {
            const ok = window.confirm(
                'Modo automático: com o bot ligado, ele vai responder TODAS as conversas novas do Comercial e os contatos novos sem setor. Confirmar?'
            );
            if (!ok) return;
        }
        salvarConfig({ modo }, 'Modo de atendimento alterado.');
    };

    const handleHorarioRestrito = (restrito) => {
        salvarConfig(
            { horario_restrito: restrito },
            restrito ? 'Atendimento por horário ativado.' : 'Atendimento por horário desativado.'
        );
    };

    const salvarHorario = () => {
        salvarConfig({}, 'Horário salvo.');
    };

    const setDia = (diaNum, patch) => {
        setConfig((c) => {
            const grade = normalizarGrade(c.horario_dias);
            grade[diaNum] = { ...grade[diaNum], ...patch };
            return { ...c, horario_dias: grade };
        });
    };

    const handleBuscar = async () => {
        if (busca.trim().length < 2) {
            toast({ title: 'Digite ao menos 2 caracteres para buscar.', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        setBuscando(true);
        try {
            const { data } = await buscarContatosBot(busca.trim());
            setResultados(data.data || []);
            if ((data.data || []).length === 0) {
                toast({ title: 'Nenhum contato encontrado na Poli.', status: 'info', duration: 3000, isClosable: true });
            }
        } catch (e) {
            toast({ title: 'Erro ao buscar contatos na Poli.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setBuscando(false);
        }
    };

    const handleAdicionar = async (c) => {
        try {
            await adicionarContatoBot({
                poli_contact_id: c.poli_contact_id,
                nome: c.nome,
                telefone: c.telefone,
            });
            toast({ title: `${c.nome} adicionado ao bot.`, status: 'success', duration: 3000, isClosable: true });
            setResultados([]);
            setBusca('');
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao adicionar o contato.', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const handleToggleContato = async (contato) => {
        try {
            await atualizarContatoBot(contato.id, !contato.ativo);
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao atualizar o contato.', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const handleRemover = async (contato) => {
        if (!window.confirm(`Remover ${contato.nome || 'este contato'} do atendimento do bot?`)) return;
        try {
            await removerContatoBot(contato.id);
            toast({ title: 'Contato removido.', status: 'info', duration: 3000, isClosable: true });
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao remover o contato.', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const handleVerificar = async () => {
        if (!config.ativo) {
            toast({ title: 'Ligue o bot primeiro para poder verificar as conversas.', status: 'warning', duration: 4000, isClosable: true });
            return;
        }
        setVerificando(true);
        try {
            await verificarConversasBot();
            toast({ title: 'Verificação iniciada — acompanhe o resultado aqui.', status: 'info', duration: 4000, isClosable: true });
            carregar(false);
        } catch (e) {
            const msg = e?.response?.data?.erro || 'Erro ao iniciar a verificação.';
            toast({ title: msg, status: 'error', duration: 4000, isClosable: true });
        } finally {
            setVerificando(false);
        }
    };

    const handleResponderItem = async (item) => {
        try {
            await responderVerificacaoBot(item.id);
            toast({ title: `Resposta do bot acionada para ${item.nome_contato || 'o contato'}.`, status: 'success', duration: 3000, isClosable: true });
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao acionar a resposta.', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const handleResponderTodas = async () => {
        const pendentes = verificacao.itens.filter((i) => !i.respondido_em).length;
        if (pendentes === 0) return;
        if (!window.confirm(`Fazer o bot responder ${pendentes} conversa(s) agora?`)) return;
        try {
            await responderTodasVerificacaoBot();
            toast({ title: 'Respostas do bot acionadas.', status: 'success', duration: 3000, isClosable: true });
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao acionar as respostas.', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const formatarData = (iso) => {
        if (!iso) return '-';
        const d = new Date(String(iso).replace(' ', 'T'));
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });
    };

    if (loading) return <Preloader />;

    const modoTodos = config.modo === 'todos';
    const verifStatus = verificacao.status;
    const verifItens = verificacao.itens || [];
    const rodando = verifStatus?.estado === 'rodando';
    const pendentes = verifItens.filter((i) => !i.respondido_em).length;

    return (
        <Box bg="gray.50" minH="100vh" pb={10} ml={{ base: 0, md: '64px' }}>
            {/* No celular o topo precisa sobrar espaço para o botão flutuante do menu. */}
            <Flex
                align="center" justify="center" gap={3} px={3}
                pt={{ base: '58px', md: 5 }} pb={{ base: 3, md: 5 }}
            >
                <Box as={FaRobot} boxSize={{ base: '22px', md: '28px' }} color={ACCENT} flexShrink={0} />
                <Heading as="h1" size={{ base: 'md', md: 'lg' }} color={ACCENT} textAlign="center">
                    Bot de Atendimento
                </Heading>
            </Flex>

            <Box mx={{ base: 2, md: 6, lg: 12 }}>
                <Tabs variant="enclosed" colorScheme="orange" isLazy defaultIndex={abaInicial}>
                    {/* São seis abas: no celular a lista rola na horizontal em vez de espremer. */}
                    <TabList
                        overflowX="auto" overflowY="hidden" flexWrap="nowrap"
                        sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
                    >
                        {ABAS.map(({ icone: Icone, curto, longo }) => (
                            <Tab
                                key={curto} flexShrink={0} whiteSpace="nowrap"
                                fontSize={{ base: 'xs', md: 'sm' }} px={{ base: 3, md: 4 }}
                            >
                                <Icone style={{ marginRight: 6 }} />
                                <Box as="span" display={{ base: 'inline', lg: 'none' }}>{curto}</Box>
                                <Box as="span" display={{ base: 'none', lg: 'inline' }}>{longo}</Box>
                            </Tab>
                        ))}
                    </TabList>
                    <TabPanels>
                        <TabPanel px={0} pt={4}>
            <VStack spacing={5} align="stretch">

                {/* Status + modo */}
                <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                    <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                        <Box>
                            <Text fontWeight="bold" fontSize="lg">Status do bot</Text>
                            <Text fontSize="sm" color="gray.500">
                                Liga e desliga o atendimento automático.
                            </Text>
                        </Box>
                        <HStack spacing={3}>
                            <Badge
                                colorScheme={!config.ativo ? 'gray' : (config.operando_agora ? 'green' : 'yellow')}
                                fontSize="0.8em" px={2} py={1} borderRadius="md"
                            >
                                {!config.ativo
                                    ? 'DESLIGADO'
                                    : (config.operando_agora ? 'ATIVO AGORA' : 'FORA DO HORÁRIO')}
                            </Badge>
                            <Switch
                                size="lg" colorScheme="orange"
                                isChecked={!!config.ativo}
                                isDisabled={savingConfig}
                                onChange={(e) => toggleBot(e.target.checked)}
                            />
                        </HStack>
                    </Flex>

                    <Divider my={4} />

                    <Text fontWeight="bold" fontSize="md" mb={2}>Modo de atendimento</Text>
                    <RadioGroup
                        value={config.modo || 'selecionados'}
                        onChange={handleModo}
                        isDisabled={savingConfig}
                        colorScheme="orange"
                    >
                        <Stack spacing={3}>
                            <Radio value="selecionados" alignItems="flex-start">
                                <Box>
                                    <Text fontWeight="medium" lineHeight={1.3}>Apenas contatos selecionados</Text>
                                    <Text fontSize="sm" color="gray.500">
                                        O bot responde só os contatos que você adicionar abaixo.
                                    </Text>
                                </Box>
                            </Radio>
                            <Radio value="todos" alignItems="flex-start">
                                <Box>
                                    <Text fontWeight="medium" lineHeight={1.3}>Atender todas as conversas (automático)</Text>
                                    <Text fontSize="sm" color="gray.500">
                                        O bot puxa e responde sozinho as conversas novas do Comercial e os contatos novos ainda sem setor.
                                    </Text>
                                </Box>
                            </Radio>
                        </Stack>
                    </RadioGroup>

                    <Divider my={4} />

                    <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                        <Box>
                            <Text fontWeight="bold" fontSize="md">Horário de funcionamento</Text>
                            <Text fontSize="sm" color="gray.500">
                                Configure por dia da semana em qual horário o bot atende automaticamente.
                            </Text>
                        </Box>
                        <Switch
                            colorScheme="orange"
                            isChecked={!!config.horario_restrito}
                            isDisabled={savingConfig}
                            onChange={(e) => handleHorarioRestrito(e.target.checked)}
                        />
                    </Flex>

                    {config.horario_restrito && (
                        <VStack align="stretch" spacing={2} mt={3}>
                            {DIAS_SEMANA.map((d) => {
                                const dia = (config.horario_dias || [])[d.v]
                                    || { ativo: false, inicio: '08:00', fim: '18:00' };
                                const fullDay = dia.ativo && dia.inicio === '00:00' && dia.fim === '23:59';
                                return (
                                    <Flex
                                        key={d.v}
                                        align="center" gap={3} wrap="wrap"
                                        py={2} px={3} borderRadius="md"
                                        bg={dia.ativo ? 'white' : 'gray.50'}
                                        border="1px solid" borderColor="gray.200"
                                    >
                                        <Text w="42px" fontWeight="medium" fontSize="sm">{d.label}</Text>
                                        <Switch
                                            size="sm" colorScheme="orange"
                                            isChecked={dia.ativo}
                                            isDisabled={savingConfig}
                                            onChange={(e) => setDia(d.v, { ativo: e.target.checked })}
                                        />
                                        {!dia.ativo ? (
                                            <Text fontSize="sm" color="gray.400">não atende</Text>
                                        ) : fullDay ? (
                                            <>
                                                <Text fontSize="sm" color="green.600" fontWeight="medium">24 horas</Text>
                                                <Button
                                                    size="xs" variant="link" colorScheme="orange"
                                                    onClick={() => setDia(d.v, { inicio: '08:00', fim: '18:00' })}
                                                >
                                                    definir horário
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Input
                                                    type="time" size="sm" maxW="120px"
                                                    value={dia.inicio || '08:00'}
                                                    isDisabled={savingConfig}
                                                    onChange={(e) => setDia(d.v, { inicio: e.target.value })}
                                                    bg="white"
                                                />
                                                <Text fontSize="sm">às</Text>
                                                <Input
                                                    type="time" size="sm" maxW="120px"
                                                    value={dia.fim || '18:00'}
                                                    isDisabled={savingConfig}
                                                    onChange={(e) => setDia(d.v, { fim: e.target.value })}
                                                    bg="white"
                                                />
                                                <Button
                                                    size="xs" variant="link" colorScheme="orange"
                                                    onClick={() => setDia(d.v, { inicio: '00:00', fim: '23:59' })}
                                                >
                                                    24h
                                                </Button>
                                            </>
                                        )}
                                    </Flex>
                                );
                            })}

                            <Flex align="center" gap={3} wrap="wrap" pt={2}>
                                <Button
                                    colorScheme="orange"
                                    size="sm"
                                    onClick={salvarHorario}
                                    isLoading={savingConfig}
                                >
                                    Salvar horário
                                </Button>
                                <Text fontSize="xs" color="gray.500">
                                    Fora do horário ou nos dias desligados o bot não responde automaticamente.
                                </Text>
                            </Flex>
                        </VStack>
                    )}
                </Box>

                {/* Comportamento do bot (system prompt) */}
                <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                    <Text fontWeight="bold" fontSize="lg" mb={1}>Comportamento do bot</Text>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                        Este é o prompt que o bot está usando agora. Edite a personalidade, regras e formato das respostas. Use <Box as="code" px={1} bg="gray.100" borderRadius="sm">{'{cliente}'}</Box> e <Box as="code" px={1} bg="gray.100" borderRadius="sm">{'{data_e_hora}'}</Box> como placeholders — são substituídos a cada atendimento.
                    </Text>
                    <Textarea
                        value={config.system_prompt || ''}
                        onChange={(e) => setConfig((c) => ({ ...c, system_prompt: e.target.value }))}
                        minH={{ base: '260px', md: '420px' }}
                        fontSize="sm"
                        fontFamily="monospace"
                    />
                    <Flex align="center" gap={3} wrap="wrap" pt={3}>
                        <Button
                            colorScheme="orange"
                            size="sm"
                            onClick={salvarPrompt}
                            isLoading={salvandoPrompt || savingConfig}
                        >
                            Salvar prompt
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={carregarPadraoNoCampo}
                            isDisabled={!systemPromptPadrao || config.system_prompt === systemPromptPadrao}
                        >
                            Restaurar padrão
                        </Button>
                    </Flex>
                </Box>

                {/* Notificação de transferência por departamento */}
                <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                    <Text fontWeight="bold" fontSize="lg" mb={1}>Notificação de transferência</Text>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                        Quando o bot transferir uma conversa para outro setor, ele avisa por WhatsApp o número
                        configurado abaixo. Use o número com DDI (ex: 5532999998888). É um número só de aviso —
                        não precisa ser o número da Poli do setor.
                    </Text>
                    {departamentos.length === 0 ? (
                        <Text fontSize="sm" color="gray.400">Nenhum setor de destino disponível.</Text>
                    ) : (
                        <VStack align="stretch" spacing={3}>
                            {departamentos.map((dep) => (
                                <Flex key={dep.id} align="center" gap={3} wrap="wrap">
                                    <Badge
                                        colorScheme="orange" fontSize="0.8em" px={2} py={1}
                                        borderRadius="md" minW="92px" textAlign="center"
                                    >
                                        {dep.nome}
                                    </Badge>
                                    <Input
                                        size="sm" maxW={{ base: 'full', md: '240px' }} bg="white"
                                        placeholder="Ex: 5532999998888"
                                        value={dep.whatsapp_notificacao || ''}
                                        onChange={(e) => setDepartamentos((ds) => ds.map((d) => (
                                            d.id === dep.id ? { ...d, whatsapp_notificacao: e.target.value } : d
                                        )))}
                                    />
                                    <Button
                                        size="sm" colorScheme="orange"
                                        isLoading={salvandoDepto === dep.id}
                                        onClick={() => salvarNumeroDepto(dep)}
                                    >
                                        Salvar
                                    </Button>
                                </Flex>
                            ))}
                        </VStack>
                    )}
                </Box>

                {/* Contatos */}
                <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                    <Text fontWeight="bold" fontSize="lg" mb={1}>Contatos atendidos</Text>

                    {modoTodos ? (
                        <Alert status="info" borderRadius="md" mt={2}>
                            <AlertIcon />
                            <Box fontSize="sm">
                                No modo automático o bot atende <b>todas as conversas do Comercial e os contatos novos sem setor</b> —
                                não precisa selecionar contatos.
                                {config.modo_todos_desde && (
                                    <> Respondendo mensagens recebidas desde <b>{formatarData(config.modo_todos_desde)}</b>.</>
                                )}
                            </Box>
                        </Alert>
                    ) : (
                        <>
                            <Text fontSize="sm" color="gray.500" mb={4}>
                                Busque um contato da Poli pelo nome ou telefone e adicione para o bot atender.
                            </Text>

                            <Stack direction={{ base: 'column', sm: 'row' }} mb={3}>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <FaSearch color="#999" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Nome ou telefone do contato..."
                                        value={busca}
                                        onChange={(e) => setBusca(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                                        bg="white"
                                    />
                                </InputGroup>
                                <Button
                                    colorScheme="orange" onClick={handleBuscar} isLoading={buscando}
                                    px={6} w={{ base: 'full', sm: 'auto' }} flexShrink={0}
                                >
                                    Buscar
                                </Button>
                            </Stack>

                            {resultados.length > 0 && (
                                <VStack
                                    align="stretch" spacing={1} mb={4}
                                    border="1px solid" borderColor="gray.200" borderRadius="md" p={2}
                                >
                                    {resultados.map((r) => (
                                        <Flex
                                            key={r.poli_contact_id} align="center" justify="space-between" gap={2}
                                            px={2} py={1.5} _hover={{ bg: 'gray.50' }} borderRadius="md"
                                        >
                                            <Box flex="1" minW={0}>
                                                <Text fontSize="sm" fontWeight="medium">{r.nome}</Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {r.telefone || 'sem telefone'} · ID {r.poli_contact_id}
                                                </Text>
                                            </Box>
                                            <Button
                                                size="xs" leftIcon={<FaPlus />} colorScheme="green" variant="outline"
                                                flexShrink={0} onClick={() => handleAdicionar(r)}
                                            >
                                                Adicionar
                                            </Button>
                                        </Flex>
                                    ))}
                                </VStack>
                            )}

                            <Divider my={3} />

                            {contatos.length === 0 ? (
                                <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>
                                    Nenhum contato selecionado — o bot não vai atender ninguém.
                                </Text>
                            ) : (
                                <VStack align="stretch" spacing={2}>
                                    {contatos.map((c) => (
                                        <Flex
                                            key={c.id} align="center" justify="space-between" gap={2} p={3}
                                            borderRadius="md" border="1px solid" borderColor="gray.200"
                                            bg={c.ativo ? 'white' : 'gray.50'}
                                        >
                                            <Box flex="1" minW={0}>
                                                <Text fontWeight="medium" noOfLines={1}>{c.nome || `Contato ${c.poli_contact_id}`}</Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {c.telefone || 'sem telefone'} · ID {c.poli_contact_id}
                                                </Text>
                                            </Box>
                                            <HStack spacing={3} flexShrink={0}>
                                                <Tooltip label={c.ativo ? 'Atendendo' : 'Pausado'}>
                                                    <Box>
                                                        <Switch
                                                            colorScheme="orange" isChecked={!!c.ativo}
                                                            onChange={() => handleToggleContato(c)}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                                <IconButton
                                                    aria-label="Remover contato" icon={<FaTrash />} size="sm"
                                                    colorScheme="red" variant="ghost"
                                                    onClick={() => handleRemover(c)}
                                                />
                                            </HStack>
                                        </Flex>
                                    ))}
                                </VStack>
                            )}
                        </>
                    )}
                </Box>

                {/* Verificar conversas em aberto */}
                <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                    <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                        <Box flex="1" minW="240px">
                            <Text fontWeight="bold" fontSize="lg">Verificar conversas em aberto</Text>
                            <Text fontSize="sm" color="gray.500">
                                A IA lê {modoTodos ? 'as conversas em aberto do Comercial e contatos novos sem setor' : 'as conversas dos contatos selecionados'} e
                                aponta quais estão de fato esperando uma resposta da loja.
                            </Text>
                        </Box>
                        <Button
                            colorScheme="orange"
                            leftIcon={<FaClipboardCheck />}
                            onClick={handleVerificar}
                            isLoading={verificando || rodando}
                            loadingText="Verificando..."
                            w={{ base: 'full', md: 'auto' }}
                        >
                            Verificar se precisa de resposta
                        </Button>
                    </Flex>

                    {rodando && (
                        <HStack color="orange.500" fontSize="sm" mt={3}>
                            <Spinner size="sm" />
                            <Text>Verificando as conversas... isso pode levar alguns minutos.</Text>
                        </HStack>
                    )}
                    {verifStatus?.estado === 'concluido' && (
                        <Text fontSize="sm" color={verifStatus.erro ? 'red.500' : 'gray.500'} mt={3}>
                            {verifStatus.erro
                                ? verifStatus.erro
                                : `Última verificação: ${formatarData(verifStatus.concluido_em)} — `
                                  + `${verifStatus.total_verificadas ?? 0} conversa(s) analisada(s), `
                                  + `${verifStatus.total_precisa ?? 0} precisam de resposta.`}
                        </Text>
                    )}

                    {verifItens.length > 0 && (
                        <>
                            <Flex align="center" justify="space-between" mt={4} mb={2} gap={2} wrap="wrap">
                                <Text fontSize="sm" fontWeight="bold" color="gray.600">
                                    Conversas que precisam de resposta
                                </Text>
                                {pendentes > 0 && (
                                    <Button size="sm" colorScheme="green" onClick={handleResponderTodas}>
                                        Responder todas ({pendentes})
                                    </Button>
                                )}
                            </Flex>
                            <VStack align="stretch" spacing={2}>
                                {verifItens.map((item) => (
                                    <Flex
                                        key={item.id} align="flex-start" justify="space-between" gap={3} p={3}
                                        borderRadius="md" border="1px solid" borderColor="gray.200"
                                        bg={item.respondido_em ? 'gray.50' : 'white'}
                                    >
                                        <Box flex="1" minW={0}>
                                            <Text fontWeight="medium">
                                                {item.nome_contato || `Contato ${item.poli_contact_id}`}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {item.telefone || 'sem telefone'}
                                            </Text>
                                            {item.motivo && (
                                                <Text fontSize="sm" color="orange.600" mt={1}>{item.motivo}</Text>
                                            )}
                                            {item.ultima_mensagem && (
                                                <Text fontSize="xs" color="gray.500" fontStyle="italic" mt={0.5}>
                                                    Última do cliente: "{item.ultima_mensagem}"
                                                </Text>
                                            )}
                                        </Box>
                                        {item.respondido_em ? (
                                            <Badge colorScheme="green" flexShrink={0}>respondido</Badge>
                                        ) : (
                                            <Button
                                                size="sm" colorScheme="green" variant="outline" flexShrink={0}
                                                onClick={() => handleResponderItem(item)}
                                            >
                                                Responder
                                            </Button>
                                        )}
                                    </Flex>
                                ))}
                            </VStack>
                        </>
                    )}
                </Box>

                {/* Atividade recente */}
                <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                    <Flex align="center" justify="space-between" mb={3}>
                        <Text fontWeight="bold" fontSize="lg">Atividade recente</Text>
                        <IconButton
                            aria-label="Atualizar" icon={<FaSyncAlt />} size="sm" variant="ghost"
                            onClick={() => carregar(false)}
                        />
                    </Flex>

                    {logs.length === 0 ? (
                        <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>
                            Nenhuma atividade ainda.
                        </Text>
                    ) : (
                        <TableContainer>
                            <Table size="sm" variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>Quando</Th>
                                        <Th>Contato</Th>
                                        <Th>Mensagem do cliente</Th>
                                        <Th>Resposta do bot</Th>
                                        <Th>Status</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {logs.map((l) => (
                                        <Tr key={l.id}>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatarData(l.created_at)}</Td>
                                            <Td fontSize="xs">{l.nome_contato || '-'}</Td>
                                            <Td fontSize="xs" maxW={{ base: '160px', md: '220px' }} whiteSpace="normal">
                                                {l.mensagem_cliente || '-'}
                                            </Td>
                                            <Td fontSize="xs" maxW={{ base: '200px', md: '340px' }} whiteSpace="normal">
                                                {l.status === 'erro'
                                                    ? (l.erro || '-')
                                                    : (l.resposta_bot || '-')}
                                            </Td>
                                            <Td>
                                                {/* maliciosa = o filtro do Jev barrou a mensagem e só a recusa saiu */}
                                                <Badge colorScheme={l.status === 'enviado' ? 'green' : (l.status === 'maliciosa' ? 'orange' : 'red')}>
                                                    {l.status}
                                                </Badge>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>

            </VStack>
                        </TabPanel>
                        <TabPanel px={0} pt={4}>
                            <BotLigacaoTab />
                        </TabPanel>
                        <TabPanel px={0} pt={4}>
                            <BotPerguntasMlTab />
                        </TabPanel>
                        <TabPanel px={0} pt={4}>
                            <PosVendaTab />
                        </TabPanel>
                        <TabPanel px={0} pt={4}>
                            <RetiraAtrasoTab />
                        </TabPanel>
                        <TabPanel px={0} pt={4}>
                            <BotLimiteCotacaoTab />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
        </Box>
    );
};

export default BotAtendimento;
